import React, { useState, useEffect, useCallback } from 'react';
import { useNavStep } from '../lib/NavHistory';
import {
  RefreshCw,
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Calendar,
  Users,
  Microscope,
  TrendingUp,
  ChevronDown,
  X,
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  Zap,
  Download,
  Upload,
  Copy,
  Star,
  Pill,
  BarChart3,
  TestTubes,
  Globe,
  Settings,
  Play,
  ExternalLink,
  Flag,
  BookOpen,
} from 'lucide-react';
import PrintButton from '../components/PrintButton';
import TranslationPanel from '../components/TranslationPanel';
import ManualSearchPanel from '../components/ManualSearchPanel';

const COLORS = {
  primary: '#2E8B98',
  teal: '#2E8B98',
  green: '#62BB46',
  orange: '#F26522',
  red: '#DC3545',
  dark: '#256F7A',
  light: '#F8F9FA',
  white: '#FFFFFF',
  border: '#e2e8f0',
  text: '#2d3748',
  textLight: '#718096',
};

const CATEGORY_OPTIONS = [
  'Cardiology',
  'Oncology',
  'Neurology',
  'Gastroenterology',
  'Rheumatology',
  'Endocrinology',
  'Infectious Disease',
  'Psychiatry',
  'Pulmonology',
  'General Surgery',
];

const STATUS_OPTIONS = [
  'Active',
  'Inactive',
  'Pending Review',
  'Archived',
  'Suspended',
];

const EVIDENCE_LEVELS = [
  'Level 1 (RCT)',
  'Level 2 (Cohort)',
  'Level 3 (Case-Control)',
  'Level 4 (Case Series)',
  'Level 5 (Expert Opinion)',
];

const GRADES = [
  'A (Strong)',
  'B (Moderate)',
  'C (Weak)',
  'D (No Evidence)',
];

const OUTCOME_OPTIONS = [
  'Excellent',
  'Good',
  'Fair',
  'Poor',
  'Inconclusive',
];

const STATUS_COLORS = {
  Active: { bg: '#dcfce7', text: '#15803d' },
  Inactive: { bg: '#f1f5f9', text: '#475569' },
  'Pending Review': { bg: '#dbeafe', text: '#1d4ed8' },
  Archived: { bg: '#e0e7ff', text: '#4338ca' },
  Suspended: { bg: '#fef3c7', text: '#b45309' },
};

const EVIDENCE_COLORS = {
  'Level 1 (RCT)': { bg: '#dcfce7', text: '#15803d' },
  'Level 2 (Cohort)': { bg: '#dbeafe', text: '#1d4ed8' },
  'Level 3 (Case-Control)': { bg: '#fef3c7', text: '#b45309' },
  'Level 4 (Case Series)': { bg: '#fecaca', text: '#991b1b' },
  'Level 5 (Expert Opinion)': { bg: '#f1f5f9', text: '#475569' },
};

// KPI Card Component
function KPICard({ label, value, icon: Icon, color }) {
  return (
    <div
      style={{
        background: COLORS.white,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 12,
        padding: 20,
        textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}
    >
      <div style={{ marginBottom: 12, color }}>
        <Icon size={24} />
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.dark, marginBottom: 4 }}>
        {value}
      </div>
      <div style={{ fontSize: 13, color: COLORS.textLight }}>{label}</div>
    </div>
  );
}

// Badge Component
function Badge({ text, bgColor, textColor, icon: Icon }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        background: bgColor,
        color: textColor,
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {Icon && <Icon size={14} />}
      {text}
    </div>
  );
}

// New Protocol Modal
function NewProtocolModal({ isOpen, onClose, onSave, isLoading }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    conditionName: '',
    category: 'General',
    icd10Code: '',
    cptCodes: '',
    hcpcsCode: '',
    snomedCode: '',
    loincCode: '',
    ndcCode: '',
    drgCode: '',
    standardOfCare: '',
    treatmentGuidelines: '',
    drugRegimen: '',
    dosage: '',
    contraindications: '',
    sideEffects: '',
    status: 'Active',
    phase: 'Clinical',
    evidenceLevel: 'Level 1 (RCT)',
    grade: 'A (Strong)',
    regulatoryStatus: 'Approved',
    principalInvestigator: '',
    institution: '',
    references: '',
    summary: '',
    studyLink: '',
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.conditionName.trim()) newErrors.conditionName = 'Condition name is required';
    return newErrors;
  };

  const handleSave = () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(formData);
    setFormData({
      title: '',
      description: '',
      conditionName: '',
      category: 'General',
      icd10Code: '',
      cptCodes: '',
      hcpcsCode: '',
      snomedCode: '',
      loincCode: '',
      ndcCode: '',
      drgCode: '',
      standardOfCare: '',
      treatmentGuidelines: '',
      drugRegimen: '',
      dosage: '',
      contraindications: '',
      sideEffects: '',
      status: 'Active',
      phase: 'Clinical',
      evidenceLevel: 'Level 1 (RCT)',
      grade: 'A (Strong)',
      regulatoryStatus: 'Approved',
      principalInvestigator: '',
      institution: '',
      references: '',
      summary: '',
      studyLink: '',
    });
    setErrors({});
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: COLORS.white,
          borderRadius: 12,
          padding: 32,
          maxWidth: 700,
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 25px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: COLORS.dark }}>
            Create New Protocol
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              color: COLORS.textLight,
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Basic Info */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: COLORS.dark, marginBottom: 12 }}>
            Basic Information
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${errors.title ? COLORS.red : COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
                placeholder="Protocol title"
              />
              {errors.title && <div style={{ fontSize: 12, color: COLORS.red, marginTop: 4 }}>{errors.title}</div>}
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                Condition Name *
              </label>
              <input
                type="text"
                name="conditionName"
                value={formData.conditionName}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${errors.conditionName ? COLORS.red : COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
                placeholder="e.g., Type 2 Diabetes"
              />
              {errors.conditionName && <div style={{ fontSize: 12, color: COLORS.red, marginTop: 4 }}>{errors.conditionName}</div>}
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                minHeight: 60,
                resize: 'vertical',
              }}
              placeholder="Protocol description"
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              >
                {CATEGORY_OPTIONS.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Medical Codes */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: COLORS.dark, marginBottom: 12 }}>
            Medical Codes
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                ICD-10 Code
              </label>
              <input
                type="text"
                name="icd10Code"
                value={formData.icd10Code}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
                placeholder="e.g., E11"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                CPT Codes
              </label>
              <input
                type="text"
                name="cptCodes"
                value={formData.cptCodes}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
                placeholder="e.g., 99213, 99214"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                HCPCS Code
              </label>
              <input
                type="text"
                name="hcpcsCode"
                value={formData.hcpcsCode}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
                placeholder="e.g., J1100"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                SNOMED CT Code
              </label>
              <input
                type="text"
                name="snomedCode"
                value={formData.snomedCode}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
                placeholder="SNOMED CT"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                LOINC Code
              </label>
              <input
                type="text"
                name="loincCode"
                value={formData.loincCode}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
                placeholder="LOINC"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                NDC / DRG Code
              </label>
              <input
                type="text"
                name="ndcCode"
                value={formData.ndcCode}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
                placeholder="NDC / DRG"
              />
            </div>
          </div>
        </div>

        {/* Protocol Details */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: COLORS.dark, marginBottom: 12 }}>
            Protocol Details
          </h3>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Standard of Care
            </label>
            <textarea
              name="standardOfCare"
              value={formData.standardOfCare}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                minHeight: 60,
                resize: 'vertical',
              }}
              placeholder="Standard treatment approach"
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Treatment Guidelines
            </label>
            <textarea
              name="treatmentGuidelines"
              value={formData.treatmentGuidelines}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                minHeight: 60,
                resize: 'vertical',
              }}
              placeholder="Treatment guidelines"
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                Drug Regimen
              </label>
              <textarea
                name="drugRegimen"
                value={formData.drugRegimen}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  minHeight: 60,
                  resize: 'vertical',
                }}
                placeholder="Medication regimen"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                Dosage
              </label>
              <textarea
                name="dosage"
                value={formData.dosage}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  minHeight: 60,
                  resize: 'vertical',
                }}
                placeholder="Dosage recommendations"
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                Contraindications
              </label>
              <textarea
                name="contraindications"
                value={formData.contraindications}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  minHeight: 60,
                  resize: 'vertical',
                }}
                placeholder="Contraindications"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                Side Effects
              </label>
              <textarea
                name="sideEffects"
                value={formData.sideEffects}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  minHeight: 60,
                  resize: 'vertical',
                }}
                placeholder="Known side effects"
              />
            </div>
          </div>
        </div>

        {/* Research Info */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: COLORS.dark, marginBottom: 12 }}>
            Research Information
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                Evidence Level
              </label>
              <select
                name="evidenceLevel"
                value={formData.evidenceLevel}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              >
                {EVIDENCE_LEVELS.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                Recommendation Grade
              </label>
              <select
                name="grade"
                value={formData.grade}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              >
                {GRADES.map((grade) => (
                  <option key={grade} value={grade}>
                    {grade}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                Principal Investigator
              </label>
              <input
                type="text"
                name="principalInvestigator"
                value={formData.principalInvestigator}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
                placeholder="PI name"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                Institution
              </label>
              <input
                type="text"
                name="institution"
                value={formData.institution}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
                placeholder="Institution"
              />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 6,
              background: COLORS.white,
              color: COLORS.text,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = COLORS.light;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = COLORS.white;
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: 6,
              background: isLoading ? COLORS.textLight : COLORS.primary,
              color: COLORS.white,
              fontSize: 13,
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (!isLoading) e.target.style.background = COLORS.dark;
            }}
            onMouseLeave={(e) => {
              if (!isLoading) e.target.style.background = COLORS.primary;
            }}
          >
            {isLoading ? 'Creating...' : 'Create Protocol'}
          </button>
        </div>
      </div>
    </div>
  );
}

// New Treatment Modal
function NewTreatmentModal({ isOpen, onClose, onSave, isLoading }) {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    patientName: '',
    condition: '',
    icd10Code: '',
    cptCode: '',
    treatment: '',
    physician: '',
    outcome: 'Inconclusive',
    notes: '',
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.patientName.trim()) newErrors.patientName = 'Patient name is required';
    if (!formData.condition.trim()) newErrors.condition = 'Condition is required';
    if (!formData.treatment.trim()) newErrors.treatment = 'Treatment is required';
    return newErrors;
  };

  const handleSave = () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(formData);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      patientName: '',
      condition: '',
      icd10Code: '',
      cptCode: '',
      treatment: '',
      physician: '',
      outcome: 'Inconclusive',
      notes: '',
    });
    setErrors({});
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
      }}
    >
      <div
        style={{
          background: COLORS.white,
          borderRadius: 12,
          padding: 32,
          maxWidth: 500,
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 25px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: COLORS.dark }}>
            Record New Treatment
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              color: COLORS.textLight,
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Date
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Patient Name *
            </label>
            <input
              type="text"
              name="patientName"
              value={formData.patientName}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${errors.patientName ? COLORS.red : COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
              placeholder="Patient name"
            />
            {errors.patientName && <div style={{ fontSize: 12, color: COLORS.red, marginTop: 4 }}>{errors.patientName}</div>}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Condition *
            </label>
            <input
              type="text"
              name="condition"
              value={formData.condition}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${errors.condition ? COLORS.red : COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
              placeholder="Condition"
            />
            {errors.condition && <div style={{ fontSize: 12, color: COLORS.red, marginTop: 4 }}>{errors.condition}</div>}
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              ICD-10 Code
            </label>
            <input
              type="text"
              name="icd10Code"
              value={formData.icd10Code}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
              placeholder="ICD-10"
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              CPT Code
            </label>
            <input
              type="text"
              name="cptCode"
              value={formData.cptCode}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
              placeholder="CPT"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Physician
            </label>
            <input
              type="text"
              name="physician"
              value={formData.physician}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
              placeholder="Physician name"
            />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Treatment *
          </label>
          <textarea
            name="treatment"
            value={formData.treatment}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${errors.treatment ? COLORS.red : COLORS.border}`,
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              minHeight: 60,
              resize: 'vertical',
            }}
            placeholder="Treatment details"
          />
          {errors.treatment && <div style={{ fontSize: 12, color: COLORS.red, marginTop: 4 }}>{errors.treatment}</div>}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Outcome
          </label>
          <select
            name="outcome"
            value={formData.outcome}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          >
            {OUTCOME_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              minHeight: 60,
              resize: 'vertical',
            }}
            placeholder="Additional notes"
          />
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 6,
              background: COLORS.white,
              color: COLORS.text,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = COLORS.light;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = COLORS.white;
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: 6,
              background: isLoading ? COLORS.textLight : COLORS.primary,
              color: COLORS.white,
              fontSize: 13,
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (!isLoading) e.target.style.background = COLORS.dark;
            }}
            onMouseLeave={(e) => {
              if (!isLoading) e.target.style.background = COLORS.primary;
            }}
          >
            {isLoading ? 'Recording...' : 'Record Treatment'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Document Management Component
function DocumentsSection({ protocolId, documents, onUpload, onDownload, onDelete, isLoading }) {
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      const base64Data = reader.result.split(',')[1];
      await onUpload({
        protocolId,
        filename: file.name,
        fileType: file.type,
        fileSize: file.size,
        fileData: base64Data,
        description: `Uploaded: ${new Date().toLocaleDateString()}`,
      });
      setSelectedFile(null);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.dark, marginBottom: 16 }}>
        Documents
      </h3>

      <div style={{ marginBottom: 20 }}>
        <label
          style={{
            display: 'inline-block',
            padding: '10px 16px',
            background: COLORS.primary,
            color: COLORS.white,
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.target.style.background = COLORS.dark;
          }}
          onMouseLeave={(e) => {
            e.target.style.background = COLORS.primary;
          }}
        >
          <Upload size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
          Upload Document
          <input
            type="file"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            disabled={isLoading}
          />
        </label>
      </div>

      {documents.length > 0 ? (
        <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: COLORS.light, borderBottom: `1px solid ${COLORS.border}` }}>
                <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                  Filename
                </th>
                <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                  Type
                </th>
                <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                  Size
                </th>
                <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                  Date
                </th>
                <th style={{ padding: 12, textAlign: 'center', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>{doc.filename}</td>
                  <td style={{ padding: 12, fontSize: 13, color: COLORS.textLight }}>{doc.fileType}</td>
                  <td style={{ padding: 12, fontSize: 13, color: COLORS.textLight }}>
                    {(doc.fileSize / 1024).toFixed(1)} KB
                  </td>
                  <td style={{ padding: 12, fontSize: 13, color: COLORS.textLight }}>
                    {new Date(doc.uploadedAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: 12, textAlign: 'center' }}>
                    <button
                      onClick={() => onDownload(doc.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: COLORS.primary,
                        cursor: 'pointer',
                        marginRight: 8,
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      <Download size={14} />
                    </button>
                    <button
                      onClick={() => onDelete(doc.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: COLORS.red,
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          style={{
            padding: 32,
            textAlign: 'center',
            background: COLORS.light,
            borderRadius: 8,
            color: COLORS.textLight,
          }}
        >
          <FileText size={32} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
          <p style={{ margin: 0, fontSize: 13 }}>No documents uploaded yet</p>
        </div>
      )}
    </div>
  );
}

// Main Component
export default function ResearchDatabasePage({ onNavigate }) {
  const [activeTab, setActiveTab] = useState('protocols');
  const [protocols, setProtocols] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [summary, setSummary] = useState({
    totalProtocols: 0,
    activeProtocols: 0,
    treatmentRecords: 0,
    avgGrade: 'B',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewProtocolModal, setShowNewProtocolModal] = useState(false);
  const [showNewTreatmentModal, setShowNewTreatmentModal] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [showDetailView, setShowDetailView] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [protocolSearch, setProtocolSearch] = useState('');
  const [protocolFilters, setProtocolFilters] = useState({
    category: '',
    status: '',
    evidenceLevel: '',
  });
  const [treatmentSearch, setTreatmentSearch] = useState('');
  const [treatmentFilters, setTreatmentFilters] = useState({
    condition: '',
    outcome: '',
  });
  const [crossCheckQuery, setCrossCheckQuery] = useState('');
  const [crossCheckResults, setCrossCheckResults] = useState(null);

  // Clinical integration state
  const [clinicalSummary, setClinicalSummary] = useState(null);
  const [clinicalPatients, setClinicalPatients] = useState(null);
  const [clinicalLoading, setClinicalLoading] = useState(false);
  const [linkingInProgress, setLinkingInProgress] = useState(null);

  // Research Sources state
  const [institutions, setInstitutions] = useState([]);
  const [searchConfigs, setSearchConfigs] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [sourcesSummary, setSourcesSummary] = useState(null);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourcesView, setSourcesView] = useState('institutions'); // institutions, configs, results
  const [showNewConfigModal, setShowNewConfigModal] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [executingSearch, setExecutingSearch] = useState(null);

  // #65 Phase C2b-1: the Sources search-RESULTS view is a master->detail sub-view
  // of this page (reached from the configs list). Register ONE Back step so global
  // Back returns to the CONFIGS list before navigating away — same handler the
  // in-view "← Back to Configs" button uses. Unconditional, top-level (no early
  // returns precede the main return). LITERAL label — never a config name, query,
  // or result content (no PHI in the sessionStorage-backed nav stack).
  useNavStep({
    active: activeTab === 'sources' && sourcesView === 'results',
    label: 'Search results',
    onBack: () => setSourcesView('configs'),
  });
  const [newConfig, setNewConfig] = useState({
    name: '', search_terms: '', condition_filter: '', mesh_terms: '',
    drug_filter: '', study_type_filter: '', date_range_months: 6,
    frequency: 'weekly', institution_ids: '',
  });
  // Institution search & download state
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [instSearchQuery, setInstSearchQuery] = useState('');
  const [instSearchResults, setInstSearchResults] = useState([]);
  const [instSearching, setInstSearching] = useState(false);
  const [selectedArticles, setSelectedArticles] = useState(new Set());
  const [savingArticles, setSavingArticles] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [instRegionFilter, setInstRegionFilter] = useState('all');
  const [showTranslation, setShowTranslation] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!window.zignagenixAPI) {
        setError('API not available');
        return;
      }

      const [summaryData, protocolsData, treatmentsData] = await Promise.all([
        window.zignagenixAPI.invoke('research-db:summary'),
        window.zignagenixAPI.invoke('research-db:protocols:list', protocolFilters),
        window.zignagenixAPI.invoke('research-db:treatments:list', treatmentFilters),
      ]);

      setSummary(
        summaryData || {
          totalProtocols: 0,
          activeProtocols: 0,
          treatmentRecords: 0,
          avgGrade: 'B',
        }
      );
      setProtocols(Array.isArray(protocolsData) ? protocolsData : []);
      setTreatments(Array.isArray(treatmentsData) ? treatmentsData : []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load research database data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProtocol = async (formData) => {
    try {
      setLoading(true);
      if (window.zignagenixAPI) {
        await window.zignagenixAPI.invoke('research-db:protocols:create', formData);
        setShowNewProtocolModal(false);
        await loadData();
      }
    } catch (err) {
      console.error('Error creating protocol:', err);
      setError('Failed to create protocol');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTreatment = async (formData) => {
    try {
      setLoading(true);
      if (window.zignagenixAPI) {
        await window.zignagenixAPI.invoke('research-db:treatments:create', formData);
        setShowNewTreatmentModal(false);
        await loadData();
      }
    } catch (err) {
      console.error('Error creating treatment:', err);
      setError('Failed to create treatment record');
    } finally {
      setLoading(false);
    }
  };

  const handleViewProtocol = async (protocol) => {
    try {
      setSelectedProtocol(protocol);
      const docsData = await window.zignagenixAPI.invoke('research-db:documents:list', protocol.id);
      setDocuments(Array.isArray(docsData) ? docsData : []);
      setShowDetailView(true);
    } catch (err) {
      console.error('Error loading protocol details:', err);
      setError('Failed to load protocol details');
    }
  };

  const handleUploadDocument = async (data) => {
    try {
      setLoading(true);
      if (window.zignagenixAPI) {
        await window.zignagenixAPI.invoke('research-db:documents:upload', data);
        const docsData = await window.zignagenixAPI.invoke('research-db:documents:list', data.protocolId);
        setDocuments(Array.isArray(docsData) ? docsData : []);
      }
    } catch (err) {
      console.error('Error uploading document:', err);
      setError('Failed to upload document');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDocument = async (docId) => {
    try {
      setLoading(true);
      if (window.zignagenixAPI) {
        const response = await window.zignagenixAPI.invoke('research-db:documents:download', docId);
        if (response && response.fileData) {
          const binaryString = atob(response.fileData);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: response.fileType });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = response.filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }
    } catch (err) {
      console.error('Error downloading document:', err);
      setError('Failed to download document');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      setLoading(true);
      if (window.zignagenixAPI && selectedProtocol) {
        await window.zignagenixAPI.invoke('research-db:documents:delete', docId);
        const docsData = await window.zignagenixAPI.invoke('research-db:documents:list', selectedProtocol.id);
        setDocuments(Array.isArray(docsData) ? docsData : []);
      }
    } catch (err) {
      console.error('Error deleting document:', err);
      setError('Failed to delete document');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDFReport = async () => {
    try {
      setLoading(true);
      if (window.zignagenixAPI && selectedProtocol) {
        const response = await window.zignagenixAPI.invoke('research-db:reports:generate-pdf', selectedProtocol.id);
        if (response && response.fileData) {
          const binaryString = atob(response.fileData);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${selectedProtocol.title}-report.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF report');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateDOCXReport = async () => {
    try {
      setLoading(true);
      if (window.zignagenixAPI && selectedProtocol) {
        const response = await window.zignagenixAPI.invoke('research-db:reports:generate-docx', selectedProtocol.id);
        if (response && response.fileData) {
          const binaryString = atob(response.fileData);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${selectedProtocol.title}-report.docx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }
    } catch (err) {
      console.error('Error generating DOCX:', err);
      setError('Failed to generate Word report');
    } finally {
      setLoading(false);
    }
  };

  const handleCrossCheck = async () => {
    if (!crossCheckQuery.trim()) {
      setError('Please enter a condition or ICD-10 code');
      return;
    }

    try {
      setLoading(true);
      if (window.zignagenixAPI) {
        const results = await window.zignagenixAPI.invoke('research-db:cross-check', {
          query: crossCheckQuery,
        });
        setCrossCheckResults(results);
      }
    } catch (err) {
      console.error('Error performing cross-check:', err);
      setError('Failed to perform cross-check');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setError(null);
      setTimeout(() => setError(null), 2000);
    });
  };

  const handleDeleteProtocol = async (protocolId) => {
    if (window.confirm('Are you sure you want to delete this protocol?')) {
      try {
        setLoading(true);
        if (window.zignagenixAPI) {
          await window.zignagenixAPI.invoke('research-db:protocols:deactivate', protocolId);
          setShowDetailView(false);
          await loadData();
        }
      } catch (err) {
        console.error('Error deleting protocol:', err);
        setError('Failed to delete protocol');
      } finally {
        setLoading(false);
      }
    }
  };

  // ── Clinical Integration Handlers ──────────────────────────────────────────
  const loadClinicalData = useCallback(async (protocolId) => {
    if (!window.zignagenixAPI || !protocolId) return;
    try {
      setClinicalLoading(true);
      const [summaryRes, patientsRes] = await Promise.all([
        window.zignagenixAPI.invoke('research-db:clinical-summary', protocolId),
        window.zignagenixAPI.invoke('research-db:protocol-patients', protocolId),
      ]);
      if (summaryRes && !summaryRes.error) setClinicalSummary(summaryRes);
      if (patientsRes && !patientsRes.error) setClinicalPatients(patientsRes);
    } catch (err) {
      console.error('Error loading clinical data:', err);
    } finally {
      setClinicalLoading(false);
    }
  }, []);

  const handleLinkPatient = async (patientId, medicationId, encounterId) => {
    if (!selectedProtocol) return;
    try {
      setLinkingInProgress(patientId);
      if (window.zignagenixAPI) {
        const result = await window.zignagenixAPI.invoke('research-db:link-medication', {
          protocolId: selectedProtocol.id,
          patientId,
          medicationId: medicationId || null,
          encounterId: encounterId || null,
        });
        if (result && result.success) {
          await loadClinicalData(selectedProtocol.id);
          await loadData();
        } else {
          setError(result?.message || 'Failed to link patient');
        }
      }
    } catch (err) {
      console.error('Error linking patient:', err);
      setError('Failed to link patient to research');
    } finally {
      setLinkingInProgress(null);
    }
  };

  // ─── Research Sources Functions ──────────────────────────────────────────
  const loadSourcesData = useCallback(async () => {
    try {
      setSourcesLoading(true);
      const api = window.zignagenixAPI;
      if (!api) return;
      const [inst, configs, summ] = await Promise.all([
        api.getResearchInstitutions(),
        api.getSearchConfigs(),
        api.getResearchSourcesSummary(),
      ]);
      setInstitutions(Array.isArray(inst) ? inst : []);
      setSearchConfigs(Array.isArray(configs) ? configs : []);
      setSourcesSummary(summ || null);
    } catch (err) {
      console.error('Error loading sources:', err);
    } finally {
      setSourcesLoading(false);
    }
  }, []);

  // Load sources data when switching to sources tab
  useEffect(() => {
    if (activeTab === 'sources' && institutions.length === 0) {
      loadSourcesData();
    }
  }, [activeTab, loadSourcesData]);

  const handleExecuteSearch = async (configId) => {
    try {
      setExecutingSearch(configId);
      const api = window.zignagenixAPI;
      const result = await api.executeSearch(configId);
      if (result && !result.error) {
        // Reload configs to update stats
        const configs = await api.getSearchConfigs();
        setSearchConfigs(Array.isArray(configs) ? configs : []);
        // Load results for this config
        const results = await api.getSearchResults({ search_config_id: configId });
        setSearchResults(Array.isArray(results) ? results : []);
        setSelectedConfig(searchConfigs.find(c => c.id === configId) || null);
        setSourcesView('results');
      } else {
        setError(result?.message || 'Search execution failed');
      }
    } catch (err) {
      console.error('Error executing search:', err);
      setError('Failed to execute search');
    } finally {
      setExecutingSearch(null);
    }
  };

  const handleViewResults = async (config) => {
    try {
      setSourcesLoading(true);
      setSelectedConfig(config);
      const results = await window.zignagenixAPI.getSearchResults({ search_config_id: config.id });
      setSearchResults(Array.isArray(results) ? results : []);
      setSourcesView('results');
    } catch (err) {
      console.error('Error loading results:', err);
    } finally {
      setSourcesLoading(false);
    }
  };

  const handleReviewResult = async (resultId, isReviewed, isFlagged, notes) => {
    try {
      await window.zignagenixAPI.reviewSearchResult(resultId, {
        is_reviewed: isReviewed, is_flagged: isFlagged, review_notes: notes,
      });
      setSearchResults(prev => prev.map(r =>
        r.id === resultId ? { ...r, is_reviewed: isReviewed, is_flagged: isFlagged, review_notes: notes } : r
      ));
    } catch (err) {
      console.error('Error reviewing result:', err);
    }
  };

  const handleImportToProtocol = async (resultId) => {
    try {
      const result = await window.zignagenixAPI.importResultToProtocol(resultId);
      if (result && !result.error) {
        loadData(); // refresh protocols
        setSearchResults(prev => prev.map(r =>
          r.id === resultId ? { ...r, linked_protocol_id: result.protocol_id } : r
        ));
      }
    } catch (err) {
      console.error('Error importing to protocol:', err);
    }
  };

  const handleCreateSearchConfig = async () => {
    try {
      const api = window.zignagenixAPI;
      const result = await api.createSearchConfig(newConfig);
      if (result && !result.error) {
        setShowNewConfigModal(false);
        setNewConfig({
          name: '', search_terms: '', condition_filter: '', mesh_terms: '',
          drug_filter: '', study_type_filter: '', date_range_months: 6,
          frequency: 'weekly', institution_ids: '',
        });
        const configs = await api.getSearchConfigs();
        setSearchConfigs(Array.isArray(configs) ? configs : []);
      }
    } catch (err) {
      console.error('Error creating search config:', err);
    }
  };

  const handleToggleConfig = async (configId, isEnabled) => {
    try {
      await window.zignagenixAPI.updateSearchConfig(configId, { is_enabled: !isEnabled });
      setSearchConfigs(prev => prev.map(c =>
        c.id === configId ? { ...c, is_enabled: !isEnabled } : c
      ));
    } catch (err) {
      console.error('Error toggling config:', err);
    }
  };

  // ─── Institution Search & Download Functions ─────────────────────────────

  const handleInstitutionSearch = async (institution) => {
    try {
      setInstSearching(true);
      setInstSearchResults([]);
      setSelectedArticles(new Set());
      const api = window.zignagenixAPI;
      const result = await api.searchInstitution(
        institution.id,
        instSearchQuery || institution.specialties?.split(',')[0]?.trim() || 'biomedical research',
        30
      );
      if (result && !result.error) {
        setInstSearchResults(result.articles || []);
      } else {
        setError(result?.message || 'Search failed');
      }
    } catch (err) {
      console.error('Error searching institution:', err);
      setError('Failed to search institution');
    } finally {
      setInstSearching(false);
    }
  };

  const handleSelectAllArticles = () => {
    if (selectedArticles.size === instSearchResults.length) {
      setSelectedArticles(new Set());
    } else {
      setSelectedArticles(new Set(instSearchResults.map((_, i) => i)));
    }
  };

  const handleToggleArticle = (index) => {
    setSelectedArticles(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleSaveSelectedArticles = async () => {
    try {
      setSavingArticles(true);
      const api = window.zignagenixAPI;
      const articles = instSearchResults.filter((_, i) => selectedArticles.has(i));
      const result = await api.bulkSaveArticles(articles, null);
      if (result && !result.error) {
        setSelectedArticles(new Set());
        loadSourcesData(); // refresh summary
        setError(null);
        // Show a brief success banner (auto-dismiss after 4s)
        setSaveMessage(`Saved ${result.saved} article(s) to database. ${result.duplicates} duplicate(s) skipped.`);
        setTimeout(() => setSaveMessage(null), 4000);
      } else {
        setError(result?.message || 'Failed to save articles');
      }
    } catch (err) {
      console.error('Error saving articles:', err);
      setError('Failed to save articles');
    } finally {
      setSavingArticles(false);
    }
  };

  const handleDownloadArticlePDF = async (resultId) => {
    try {
      const api = window.zignagenixAPI;
      const result = await api.downloadArticle(resultId);
      if (result && result.pdfUrl) {
        window.open(result.pdfUrl, '_blank');
      } else {
        alert(result?.message || 'No PDF available for this article');
      }
    } catch (err) {
      console.error('Error downloading article:', err);
    }
  };

  const filteredProtocols = protocols.filter((p) => {
    let match = true;
    if (protocolSearch && !p.title.toLowerCase().includes(protocolSearch.toLowerCase()) &&
        !p.conditionName?.toLowerCase().includes(protocolSearch.toLowerCase())) {
      match = false;
    }
    if (protocolFilters.category && p.category !== protocolFilters.category) match = false;
    if (protocolFilters.status && p.status !== protocolFilters.status) match = false;
    if (protocolFilters.evidenceLevel && p.evidenceLevel !== protocolFilters.evidenceLevel) match = false;
    return match;
  });

  const filteredTreatments = treatments.filter((t) => {
    let match = true;
    if (treatmentSearch && !t.condition?.toLowerCase().includes(treatmentSearch.toLowerCase()) &&
        !t.patientName?.toLowerCase().includes(treatmentSearch.toLowerCase())) {
      match = false;
    }
    if (treatmentFilters.condition && t.condition !== treatmentFilters.condition) match = false;
    if (treatmentFilters.outcome && t.outcome !== treatmentFilters.outcome) match = false;
    return match;
  });

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ margin: '0 0 4px 0', fontSize: 28, fontWeight: 700, color: COLORS.dark }}>
            Research Database
          </h1>
          <p style={{ margin: 0, fontSize: 14, color: COLORS.textLight }}>
            Manage protocols, treatments, and clinical evidence
          </p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 16px',
            border: `1px solid ${COLORS.border}`,
            borderRadius: 6,
            background: COLORS.white,
            color: COLORS.primary,
            fontSize: 13,
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            if (!loading) e.target.style.background = COLORS.light;
          }}
          onMouseLeave={(e) => {
            e.target.style.background = COLORS.white;
          }}
        >
          <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          Refresh
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div
          style={{
            marginBottom: 20,
            padding: 12,
            background: '#fef2f2',
            border: `1px solid ${COLORS.red}`,
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            color: COLORS.red,
            fontSize: 13,
          }}
        >
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
        <KPICard
          label="Total Protocols"
          value={summary.totalProtocols}
          icon={TestTubes}
          color={COLORS.primary}
        />
        <KPICard
          label="Active Protocols"
          value={summary.activeProtocols}
          icon={CheckCircle}
          color={COLORS.green}
        />
        <KPICard
          label="Treatment Records"
          value={summary.treatmentRecords}
          icon={BarChart3}
          color={COLORS.orange}
        />
        <KPICard
          label="Avg. Grade"
          value={summary.avgGrade}
          icon={TrendingUp}
          color={COLORS.primary}
        />
      </div>

      {/* Tab Navigation */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: `2px solid ${COLORS.border}`,
          marginBottom: 24,
        }}
      >
        {['protocols', 'treatments', 'clinical data', 'sources', 'cross-check', 'documents', 'reports'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '12px 16px',
              border: 'none',
              background: 'none',
              fontSize: 13,
              fontWeight: 600,
              color: activeTab === tab ? COLORS.primary : COLORS.textLight,
              cursor: 'pointer',
              borderBottom: activeTab === tab ? `3px solid ${COLORS.primary}` : 'none',
              transition: 'all 0.2s',
              marginBottom: -2,
              textTransform: 'capitalize',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab) e.target.style.color = COLORS.text;
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab) e.target.style.color = COLORS.textLight;
            }}
          >
            {tab.replace('-', ' ')}
          </button>
        ))}
        {/* Ongoing Research cross-link */}
        <button
          onClick={() => onNavigate && onNavigate('research')}
          style={{
            padding: '12px 16px',
            border: 'none',
            background: 'none',
            fontSize: 13,
            fontWeight: 600,
            color: COLORS.textLight,
            cursor: 'pointer',
            borderBottom: 'none',
            transition: 'all 0.2s',
            marginBottom: -2,
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = COLORS.primary; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = COLORS.textLight; }}
          title="Go to Research Studies (Ongoing Research)"
        >
          <Microscope size={14} />
          Ongoing Research →
        </button>
      </div>

      {/* Protocols Tab */}
      {activeTab === 'protocols' && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Search size={18} style={{ color: COLORS.textLight }} />
              <input
                type="text"
                placeholder="Search protocols..."
                value={protocolSearch}
                onChange={(e) => setProtocolSearch(e.target.value)}
                style={{
                  flex: 1,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  padding: '10px 12px',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <select
              value={protocolFilters.category}
              onChange={(e) => setProtocolFilters({ ...protocolFilters, category: e.target.value })}
              style={{
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                padding: '10px 12px',
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            >
              <option value="">All Categories</option>
              {CATEGORY_OPTIONS.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            <select
              value={protocolFilters.status}
              onChange={(e) => setProtocolFilters({ ...protocolFilters, status: e.target.value })}
              style={{
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                padding: '10px 12px',
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            >
              <option value="">All Status</option>
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            <button
              onClick={() => setShowNewProtocolModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 16px',
                border: 'none',
                borderRadius: 6,
                background: COLORS.primary,
                color: COLORS.white,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = COLORS.dark;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = COLORS.primary;
              }}
            >
              <Plus size={14} />
              New Protocol
            </button>
          </div>

          {/* Protocols Table */}
          {filteredProtocols.length > 0 ? (
            <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: COLORS.light, borderBottom: `1px solid ${COLORS.border}` }}>
                    <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                      Title
                    </th>
                    <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                      Condition
                    </th>
                    <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                      ICD-10
                    </th>
                    <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                      Status
                    </th>
                    <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                      Evidence
                    </th>
                    <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                      Grade
                    </th>
                    <th style={{ padding: 12, textAlign: 'center', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProtocols.map((protocol) => (
                    <tr key={protocol.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: 12, fontSize: 13, color: COLORS.text, fontWeight: 500 }}>
                        {protocol.title}
                      </td>
                      <td style={{ padding: 12, fontSize: 13, color: COLORS.textLight }}>
                        {protocol.conditionName}
                      </td>
                      <td style={{ padding: 12, fontSize: 13, color: COLORS.textLight }}>
                        {protocol.icd10Code}
                      </td>
                      <td style={{ padding: 12 }}>
                        <Badge
                          text={protocol.status}
                          bgColor={STATUS_COLORS[protocol.status]?.bg || COLORS.light}
                          textColor={STATUS_COLORS[protocol.status]?.text || COLORS.text}
                        />
                      </td>
                      <td style={{ padding: 12, fontSize: 12, color: COLORS.textLight }}>
                        {protocol.evidenceLevel}
                      </td>
                      <td style={{ padding: 12, fontSize: 13, fontWeight: 600, color: COLORS.primary }}>
                        {protocol.grade}
                      </td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        <button
                          onClick={() => handleViewProtocol(protocol)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: COLORS.primary,
                            cursor: 'pointer',
                            marginRight: 8,
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteProtocol(protocol.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: COLORS.red,
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div
              style={{
                padding: 40,
                textAlign: 'center',
                background: COLORS.light,
                borderRadius: 8,
                color: COLORS.textLight,
              }}
            >
              <Microscope size={32} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
              <p style={{ margin: 0, fontSize: 13 }}>No protocols found</p>
            </div>
          )}
        </div>
      )}

      {/* Treatments Tab */}
      {activeTab === 'treatments' && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Search size={18} style={{ color: COLORS.textLight }} />
              <input
                type="text"
                placeholder="Search treatments..."
                value={treatmentSearch}
                onChange={(e) => setTreatmentSearch(e.target.value)}
                style={{
                  flex: 1,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  padding: '10px 12px',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <select
              value={treatmentFilters.outcome}
              onChange={(e) => setTreatmentFilters({ ...treatmentFilters, outcome: e.target.value })}
              style={{
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                padding: '10px 12px',
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            >
              <option value="">All Outcomes</option>
              {OUTCOME_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            <button
              onClick={() => setShowNewTreatmentModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 16px',
                border: 'none',
                borderRadius: 6,
                background: COLORS.primary,
                color: COLORS.white,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = COLORS.dark;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = COLORS.primary;
              }}
            >
              <Plus size={14} />
              New Treatment
            </button>
          </div>

          {/* Treatments Table */}
          {filteredTreatments.length > 0 ? (
            <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: COLORS.light, borderBottom: `1px solid ${COLORS.border}` }}>
                    <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                      Date
                    </th>
                    <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                      Patient
                    </th>
                    <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                      Condition
                    </th>
                    <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                      ICD-10
                    </th>
                    <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                      Treatment
                    </th>
                    <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                      Physician
                    </th>
                    <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                      Outcome
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTreatments.map((treatment) => (
                    <tr key={treatment.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>
                        {new Date(treatment.date).toLocaleDateString()}
                      </td>
                      <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>
                        {treatment.patientName}
                      </td>
                      <td style={{ padding: 12, fontSize: 13, color: COLORS.textLight }}>
                        {treatment.condition}
                      </td>
                      <td style={{ padding: 12, fontSize: 13, color: COLORS.textLight }}>
                        {treatment.icd10Code}
                      </td>
                      <td style={{ padding: 12, fontSize: 13, color: COLORS.textLight }}>
                        {treatment.treatment}
                      </td>
                      <td style={{ padding: 12, fontSize: 13, color: COLORS.textLight }}>
                        {treatment.physician}
                      </td>
                      <td style={{ padding: 12 }}>
                        <Badge
                          text={treatment.outcome}
                          bgColor={COLORS.light}
                          textColor={COLORS.text}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div
              style={{
                padding: 40,
                textAlign: 'center',
                background: COLORS.light,
                borderRadius: 8,
                color: COLORS.textLight,
              }}
            >
              <Pill size={32} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
              <p style={{ margin: 0, fontSize: 13 }}>No treatment records found</p>
            </div>
          )}
        </div>
      )}

      {/* Clinical Data Tab */}
      {activeTab === 'clinical data' && (
        <div>
          {/* Protocol selector for clinical view */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 8, display: 'block' }}>
              Select Protocol to View Clinical Connections
            </label>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <select
                value={selectedProtocol?.id || ''}
                onChange={(e) => {
                  const proto = protocols.find(p => p.id === parseInt(e.target.value));
                  if (proto) {
                    setSelectedProtocol(proto);
                    loadClinicalData(proto.id);
                  }
                }}
                style={{
                  flex: 1, maxWidth: 400, padding: '10px 12px', border: `1px solid ${COLORS.border}`,
                  borderRadius: 6, fontSize: 13, fontFamily: 'inherit',
                }}
              >
                <option value="">Choose a protocol...</option>
                {protocols.map((p) => (
                  <option key={p.id} value={p.id}>{p.title} — {p.condition_name || p.conditionName || 'N/A'}</option>
                ))}
              </select>
              {clinicalLoading && (
                <RefreshCw size={16} style={{ color: COLORS.primary, animation: 'spin 1s linear infinite' }} />
              )}
            </div>
          </div>

          {selectedProtocol && clinicalSummary && (
            <>
              {/* Clinical Overview Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
                <div style={{ background: '#dbeafe', borderRadius: 8, padding: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1d4ed8' }}>
                    {clinicalSummary.clinicalOverview?.matchingEncPatients || 0}
                  </div>
                  <div style={{ fontSize: 12, color: '#3b82f6', marginTop: 4 }}>Patients w/ Encounters</div>
                </div>
                <div style={{ background: '#dcfce7', borderRadius: 8, padding: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#15803d' }}>
                    {clinicalSummary.clinicalOverview?.matchingMedPatients || 0}
                  </div>
                  <div style={{ fontSize: 12, color: '#22c55e', marginTop: 4 }}>Patients on Medications</div>
                </div>
                <div style={{ background: '#fef3c7', borderRadius: 8, padding: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#b45309' }}>
                    {clinicalSummary.clinicalOverview?.matchingMedications || 0}
                  </div>
                  <div style={{ fontSize: 12, color: '#f59e0b', marginTop: 4 }}>Active Prescriptions</div>
                </div>
                <div style={{ background: '#e0e7ff', borderRadius: 8, padding: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#4338ca' }}>
                    {clinicalSummary.treatmentOutcomes?.uniquePatients || 0}
                  </div>
                  <div style={{ fontSize: 12, color: '#6366f1', marginTop: 4 }}>Linked to Research</div>
                </div>
              </div>

              {/* Treatment Outcomes bar */}
              {clinicalSummary.treatmentOutcomes?.total > 0 && (
                <div style={{ background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 16, marginBottom: 20 }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: COLORS.dark }}>Treatment Outcomes</h4>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    {[
                      { label: 'Excellent', count: clinicalSummary.treatmentOutcomes.excellent, color: '#15803d' },
                      { label: 'Good', count: clinicalSummary.treatmentOutcomes.good, color: '#22c55e' },
                      { label: 'Fair', count: clinicalSummary.treatmentOutcomes.fair, color: '#f59e0b' },
                      { label: 'Poor', count: clinicalSummary.treatmentOutcomes.poor, color: '#dc2626' },
                      { label: 'In Progress', count: clinicalSummary.treatmentOutcomes.inProgress, color: '#6366f1' },
                    ].map((o) => (
                      <div key={o.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: o.color }} />
                        <span style={{ fontSize: 13, color: COLORS.text }}>{o.label}: <strong>{o.count}</strong></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Medications for this condition */}
              {clinicalSummary.topMedications?.length > 0 && (
                <div style={{ background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 16, marginBottom: 20 }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: COLORS.dark }}>
                    <Pill size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                    Top Medications Prescribed for this Condition
                  </h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                          <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, color: COLORS.textLight }}>Drug Name</th>
                          <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, color: COLORS.textLight }}>Dosage</th>
                          <th style={{ textAlign: 'center', padding: '8px 12px', fontSize: 12, color: COLORS.textLight }}>Patients</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clinicalSummary.topMedications.map((med, i) => (
                          <tr key={i} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                            <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 600, color: COLORS.text }}>{med.drug_name}</td>
                            <td style={{ padding: '10px 12px', fontSize: 13, color: COLORS.textLight }}>{med.dosage || '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: 13, textAlign: 'center', color: COLORS.primary, fontWeight: 600 }}>
                              {med.patient_count}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Matching Patients Table */}
              {clinicalPatients && clinicalPatients.patients?.length > 0 && (
                <div style={{ background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 16 }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: COLORS.dark }}>
                    <Users size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                    Matching Patients ({clinicalPatients.totalPatients})
                  </h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                          <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, color: COLORS.textLight }}>Patient</th>
                          <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, color: COLORS.textLight }}>MRN</th>
                          <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, color: COLORS.textLight }}>Match Source</th>
                          <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, color: COLORS.textLight }}>Medications</th>
                          <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, color: COLORS.textLight }}>Encounters</th>
                          <th style={{ textAlign: 'center', padding: '8px 12px', fontSize: 12, color: COLORS.textLight }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clinicalPatients.patients.map((pt) => (
                          <tr key={pt.patientId} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                            <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                              {pt.firstName} {pt.lastName}
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: 13, color: COLORS.textLight, fontFamily: 'monospace' }}>
                              {pt.mrn}
                            </td>
                            <td style={{ padding: '10px 12px' }}>
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {pt.encounters?.length > 0 && (
                                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#dbeafe', color: '#1d4ed8' }}>
                                    Encounters ({pt.encounters.length})
                                  </span>
                                )}
                                {pt.medications?.length > 0 && (
                                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#dcfce7', color: '#15803d' }}>
                                    Medications ({pt.medications.length})
                                  </span>
                                )}
                                {pt.problems?.length > 0 && (
                                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#fef3c7', color: '#b45309' }}>
                                    Problems ({pt.problems.length})
                                  </span>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: 12, color: COLORS.textLight }}>
                              {pt.medications?.length > 0 ? pt.medications.map(m => m.drugName).join(', ') : '—'}
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: 12, color: COLORS.textLight }}>
                              {pt.encounters?.length > 0 ? pt.encounters.length + ' visit(s)' : '—'}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              <button
                                onClick={() => handleLinkPatient(
                                  pt.patientId,
                                  pt.medications?.[0]?.medicationId || null,
                                  pt.encounters?.[0]?.encounterId || null
                                )}
                                disabled={linkingInProgress === pt.patientId}
                                style={{
                                  padding: '6px 12px', borderRadius: 4, border: `1px solid ${COLORS.primary}`,
                                  background: COLORS.white, color: COLORS.primary, fontSize: 12, fontWeight: 600,
                                  cursor: linkingInProgress === pt.patientId ? 'not-allowed' : 'pointer',
                                  opacity: linkingInProgress === pt.patientId ? 0.5 : 1,
                                }}
                              >
                                {linkingInProgress === pt.patientId ? 'Linking...' : '+ Link to Research'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {clinicalPatients && clinicalPatients.patients?.length === 0 && !clinicalLoading && (
                <div style={{ textAlign: 'center', padding: 40, color: COLORS.textLight }}>
                  <Users size={32} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
                  <p style={{ margin: 0, fontSize: 13 }}>No matching patients found for this protocol's condition</p>
                  <p style={{ margin: '4px 0 0', fontSize: 12 }}>Patients are matched by ICD-10 codes, conditions, and medications</p>
                </div>
              )}
            </>
          )}

          {!selectedProtocol && (
            <div style={{ textAlign: 'center', padding: 60, color: COLORS.textLight }}>
              <Microscope size={40} style={{ margin: '0 auto 16px', opacity: 0.4 }} />
              <p style={{ margin: 0, fontSize: 14 }}>Select a protocol above to view connected patient treatments and medications</p>
            </div>
          )}
        </div>
      )}

      {/* Sources Tab */}
      {activeTab === 'sources' && (
        <div>
          {/* Sources Sub-Navigation */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            {[
              { key: 'institutions', label: 'Research Institutions', icon: Globe },
              { key: 'configs', label: 'Search Configurations', icon: Settings },
              { key: 'results', label: 'Search Results', icon: BookOpen },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => {
                  setSourcesView(key);
                  if (key === 'institutions' || key === 'configs') loadSourcesData();
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
                  borderRadius: 6, border: `1px solid ${sourcesView === key ? COLORS.primary : COLORS.border}`,
                  background: sourcesView === key ? '#e0f2fe' : COLORS.white,
                  color: sourcesView === key ? COLORS.primary : COLORS.text,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                }}
              >
                <Icon size={14} />
                {label}
              </button>
            ))}
            <button
              onClick={loadSourcesData}
              disabled={sourcesLoading}
              style={{
                marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 6, border: `1px solid ${COLORS.border}`,
                background: COLORS.white, color: COLORS.text, fontSize: 12, cursor: 'pointer',
              }}
            >
              <RefreshCw size={14} style={sourcesLoading ? { animation: 'spin 1s linear infinite' } : {}} />
              Refresh
            </button>
          </div>

          {/* Institutions View */}
          {sourcesView === 'institutions' && (
            <div>
              {/* Summary cards */}
              {sourcesSummary && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
                  <div style={{ background: '#dbeafe', borderRadius: 8, padding: 14, textAlign: 'center' }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#1d4ed8' }}>{sourcesSummary.institutions || 0}</div>
                    <div style={{ fontSize: 11, color: '#3b82f6', marginTop: 2 }}>Institutions</div>
                  </div>
                  <div style={{ background: '#dcfce7', borderRadius: 8, padding: 14, textAlign: 'center' }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#15803d' }}>{sourcesSummary.searchConfigs || 0}</div>
                    <div style={{ fontSize: 11, color: '#22c55e', marginTop: 2 }}>Search Configs</div>
                  </div>
                  <div style={{ background: '#fef3c7', borderRadius: 8, padding: 14, textAlign: 'center' }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#b45309' }}>{sourcesSummary.totalResults || 0}</div>
                    <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 2 }}>Articles in Database</div>
                  </div>
                  <div style={{ background: '#fce7f3', borderRadius: 8, padding: 14, textAlign: 'center' }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#be185d' }}>{sourcesSummary.activeSearches || 0}</div>
                    <div style={{ fontSize: 11, color: '#ec4899', marginTop: 2 }}>Active Searches</div>
                  </div>
                </div>
              )}

              {/* Region filter tabs */}
              {!selectedInstitution && (
                <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
                  {['all', 'North America', 'Europe', 'Asia-Pacific', 'Global'].map(region => (
                    <button key={region} onClick={() => setInstRegionFilter(region)} style={{
                      padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      border: `1px solid ${instRegionFilter === region ? COLORS.primary : COLORS.border}`,
                      background: instRegionFilter === region ? '#e0f2fe' : COLORS.white,
                      color: instRegionFilter === region ? COLORS.primary : COLORS.text,
                    }}>
                      {region === 'all' ? 'All Regions' : region}
                      <span style={{ marginLeft: 4, fontSize: 10, opacity: 0.7 }}>
                        ({region === 'all' ? institutions.length : institutions.filter(i => i.region === region).length})
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Institution Detail / Search Panel */}
              {selectedInstitution && (
                <div style={{ marginBottom: 20, border: `2px solid ${COLORS.primary}`, borderRadius: 10, padding: 20, background: '#f8fafc' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: COLORS.dark }}>{selectedInstitution.name}</h3>
                      <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#e0f2fe', color: '#0369a1' }}>{selectedInstitution.country}</span>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#f3e8ff', color: '#7c3aed' }}>{selectedInstitution.region}</span>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#dcfce7', color: '#15803d' }}>Multi-API Search</span>
                      </div>
                      <p style={{ margin: '8px 0 0', fontSize: 12, color: COLORS.textLight, maxWidth: 600 }}>{selectedInstitution.description}</p>
                    </div>
                    <button onClick={() => { setSelectedInstitution(null); setInstSearchResults([]); setSelectedArticles(new Set()); }}
                      style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.white, fontSize: 12, cursor: 'pointer' }}>
                      Close
                    </button>
                  </div>

                  {/* Search bar */}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    <input
                      type="text"
                      placeholder={`Search ${selectedInstitution.short_name || selectedInstitution.name} publications...`}
                      value={instSearchQuery}
                      onChange={(e) => setInstSearchQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleInstitutionSearch(selectedInstitution); }}
                      style={{
                        flex: 1, padding: '10px 14px', borderRadius: 6, border: `1px solid ${COLORS.border}`,
                        fontSize: 13, outline: 'none',
                      }}
                    />
                    <button
                      onClick={() => handleInstitutionSearch(selectedInstitution)}
                      disabled={instSearching}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 6,
                        border: 'none', background: COLORS.primary, color: COLORS.white, fontSize: 13, fontWeight: 600, cursor: instSearching ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {instSearching ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={14} />}
                      {instSearching ? 'Searching 4 APIs...' : 'Search'}
                    </button>
                  </div>

                  {/* Search results list */}
                  {instSearchResults.length > 0 && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.dark }}>
                          Found {instSearchResults.length} articles
                          <span style={{ fontWeight: 400, color: COLORS.textLight, marginLeft: 8 }}>
                            (via PubMed, CrossRef, Europe PMC, Semantic Scholar)
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={handleSelectAllArticles} style={{
                            padding: '5px 12px', borderRadius: 4, border: `1px solid ${COLORS.border}`,
                            background: COLORS.white, fontSize: 11, cursor: 'pointer',
                          }}>
                            {selectedArticles.size === instSearchResults.length ? 'Deselect All' : 'Select All'}
                          </button>
                          <button
                            onClick={handleSaveSelectedArticles}
                            disabled={selectedArticles.size === 0 || savingArticles}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 4, padding: '5px 14px', borderRadius: 4,
                              border: 'none', background: selectedArticles.size > 0 ? COLORS.green : '#d1d5db',
                              color: COLORS.white, fontSize: 11, fontWeight: 600,
                              cursor: selectedArticles.size > 0 ? 'pointer' : 'not-allowed',
                            }}
                          >
                            <Download size={12} />
                            {savingArticles ? 'Saving...' : `Save ${selectedArticles.size} to Database`}
                          </button>
                        </div>
                      </div>

                      {saveMessage && (
                        <div style={{ padding: '8px 14px', background: '#dcfce7', border: '1px solid #86efac', borderRadius: 6, margin: '8px 0', fontSize: 12, color: '#166534', fontWeight: 600 }}>
                          {saveMessage}
                        </div>
                      )}

                      <div style={{ maxHeight: 500, overflowY: 'auto', border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
                        {instSearchResults.map((article, idx) => (
                          <div key={idx} style={{
                            display: 'flex', gap: 10, padding: '12px 14px', borderBottom: `1px solid ${COLORS.border}`,
                            background: selectedArticles.has(idx) ? '#eff6ff' : COLORS.white, cursor: 'pointer',
                            transition: 'background 0.15s',
                          }} onClick={() => handleToggleArticle(idx)}>
                            <input type="checkbox" checked={selectedArticles.has(idx)} onChange={() => handleToggleArticle(idx)}
                              style={{ marginTop: 2, accentColor: COLORS.primary, cursor: 'pointer' }} onClick={(e) => e.stopPropagation()} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.dark, lineHeight: 1.4, marginBottom: 4 }}>
                                {article.title}
                              </div>
                              <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 4 }}>
                                {article.authors?.substring(0, 80)}{article.authors?.length > 80 ? '...' : ''}
                              </div>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {article.journal && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: '#e0f2fe', color: '#0369a1' }}>{article.journal.substring(0, 40)}</span>}
                                {article.publicationDate && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: '#f3e8ff', color: '#7c3aed' }}>{article.publicationDate}</span>}
                                {article.sourceApi && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: '#fef3c7', color: '#b45309' }}>{article.sourceApi}</span>}
                                {article.isOpenAccess && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: '#dcfce7', color: '#15803d' }}>Open Access</span>}
                                {article.citationCount > 0 && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: '#fee2e2', color: '#dc2626' }}>{article.citationCount} citations</span>}
                                {article.doi && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: '#f1f5f9', color: '#64748b' }}>DOI</span>}
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                              {article.url && (
                                <a href={article.url} target="_blank" rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ fontSize: 11, color: COLORS.primary, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}>
                                  <ExternalLink size={10} /> View
                                </a>
                              )}
                              {article.pdfUrl && (
                                <a href={article.pdfUrl} target="_blank" rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ fontSize: 11, color: '#dc2626', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}>
                                  <Download size={10} /> PDF
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {instSearchResults.length === 0 && !instSearching && (
                    <div style={{ textAlign: 'center', padding: 30, color: COLORS.textLight }}>
                      <Search size={24} style={{ margin: '0 auto 8px', opacity: 0.4, display: 'block' }} />
                      <p style={{ margin: 0, fontSize: 13 }}>Enter a search query and click Search to find articles from this institution</p>
                      <p style={{ margin: '4px 0 0', fontSize: 11 }}>Searches across PubMed, CrossRef, Europe PMC, and Semantic Scholar simultaneously</p>
                    </div>
                  )}
                </div>
              )}

              {/* Institution cards grid */}
              {!selectedInstitution && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                  {institutions
                    .filter(inst => instRegionFilter === 'all' || inst.region === instRegionFilter)
                    .map((inst) => (
                    <div
                      key={inst.id}
                      style={{
                        border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 16,
                        background: COLORS.white, transition: 'all 0.2s', cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = COLORS.primary; }}
                      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = COLORS.border; }}
                      onClick={() => { setSelectedInstitution(inst); setInstSearchQuery(''); setInstSearchResults([]); setSelectedArticles(new Set()); }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <div>
                          <h4 style={{ margin: '0 0 2px 0', fontSize: 14, fontWeight: 700, color: COLORS.dark }}>{inst.name}</h4>
                          <span style={{ fontSize: 11, color: COLORS.textLight }}>{inst.short_name}</span>
                        </div>
                        <span style={{
                          fontSize: 10, padding: '2px 8px', borderRadius: 10,
                          background: inst.is_active ? '#dcfce7' : '#fee2e2',
                          color: inst.is_active ? '#15803d' : '#dc2626',
                          fontWeight: 600,
                        }}>
                          {inst.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#e0f2fe', color: '#0369a1' }}>{inst.country}</span>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#f3e8ff', color: '#7c3aed' }}>{inst.region}</span>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#fef3c7', color: '#b45309' }}>{inst.api_source || inst.api_type}</span>
                      </div>
                      <p style={{ margin: '0 0 8px 0', fontSize: 12, color: COLORS.textLight, lineHeight: 1.5 }}>
                        {inst.description?.substring(0, 120)}{inst.description?.length > 120 ? '...' : ''}
                      </p>
                      <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 6 }}>
                        <strong>Specialties:</strong> {inst.specialties || 'N/A'}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {inst.website_url && (
                          <a href={inst.website_url} target="_blank" rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{ fontSize: 12, color: COLORS.primary, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <ExternalLink size={12} /> Website
                          </a>
                        )}
                        <span style={{ fontSize: 11, color: COLORS.primary, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Search size={12} /> Click to Search
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {institutions.length === 0 && !sourcesLoading && (
                <div style={{ textAlign: 'center', padding: 40, color: COLORS.textLight }}>
                  <Globe size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
                  <p style={{ margin: 0, fontSize: 13 }}>No research institutions loaded yet</p>
                  <p style={{ margin: '4px 0 0', fontSize: 12 }}>Institutions will appear after the migration runs</p>
                </div>
              )}
            </div>
          )}

          {/* Search Configurations View */}
          {sourcesView === 'configs' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: COLORS.dark }}>Automated Search Configurations</h3>
                <button
                  onClick={() => setShowNewConfigModal(true)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
                    borderRadius: 6, border: 'none', background: COLORS.primary,
                    color: COLORS.white, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  <Plus size={14} /> New Search
                </button>
              </div>

              {searchConfigs.map((config) => (
                <div
                  key={config.id}
                  style={{
                    border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 16,
                    marginBottom: 12, background: COLORS.white,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: COLORS.dark }}>{config.name}</h4>
                        <span style={{
                          fontSize: 10, padding: '2px 8px', borderRadius: 10,
                          background: config.is_enabled ? '#dcfce7' : '#fee2e2',
                          color: config.is_enabled ? '#15803d' : '#dc2626', fontWeight: 600,
                        }}>
                          {config.is_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}>
                          {config.frequency}
                        </span>
                      </div>
                      {config.description && (
                        <p style={{ margin: '0 0 8px 0', fontSize: 12, color: COLORS.textLight }}>{config.description}</p>
                      )}
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: COLORS.text }}>
                        <span><strong>Terms:</strong> {config.search_terms}</span>
                        {config.condition_filter && <span><strong>Condition:</strong> {config.condition_filter}</span>}
                        {config.drug_filter && <span><strong>Drug:</strong> {config.drug_filter}</span>}
                        {config.mesh_terms && <span><strong>MeSH:</strong> {config.mesh_terms}</span>}
                        {config.study_type_filter && <span><strong>Type:</strong> {config.study_type_filter}</span>}
                        <span><strong>Range:</strong> {config.date_range_months}mo</span>
                      </div>
                      <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 11, color: COLORS.textLight }}>
                        <span>Total results: {config.total_results_found || 0}</span>
                        <span>Last run: {config.last_run_at ? new Date(config.last_run_at).toLocaleDateString() : 'Never'}</span>
                        <span>Next run: {config.next_run_at ? new Date(config.next_run_at).toLocaleDateString() : 'Not scheduled'}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => handleToggleConfig(config.id, config.is_enabled)}
                        style={{
                          padding: '6px 12px', borderRadius: 4, border: `1px solid ${COLORS.border}`,
                          background: COLORS.white, color: COLORS.text, fontSize: 12, cursor: 'pointer',
                        }}
                      >
                        {config.is_enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        onClick={() => handleExecuteSearch(config.id)}
                        disabled={executingSearch === config.id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 4,
                          border: 'none', background: COLORS.green, color: COLORS.white,
                          fontSize: 12, fontWeight: 600, cursor: executingSearch === config.id ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {executingSearch === config.id ? (
                          <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} />
                        ) : (
                          <Play size={12} />
                        )}
                        {executingSearch === config.id ? 'Searching...' : 'Run Now'}
                      </button>
                      <button
                        onClick={() => handleViewResults(config)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 4,
                          border: `1px solid ${COLORS.primary}`, background: COLORS.white, color: COLORS.primary,
                          fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        }}
                      >
                        <Eye size={12} /> Results
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {searchConfigs.length === 0 && !sourcesLoading && (
                <div style={{ textAlign: 'center', padding: 40, color: COLORS.textLight }}>
                  <Settings size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
                  <p style={{ margin: 0, fontSize: 13 }}>No search configurations yet</p>
                  <p style={{ margin: '4px 0 0', fontSize: 12 }}>Create a new search to start pulling research from institutions</p>
                </div>
              )}
            </div>
          )}

          {/* Search Results View */}
          {sourcesView === 'results' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: COLORS.dark }}>
                    Search Results {selectedConfig ? `— ${selectedConfig.name}` : ''}
                  </h3>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: COLORS.textLight }}>
                    {searchResults.length} result(s) found
                  </p>
                </div>
                <button
                  onClick={() => setSourcesView('configs')}
                  style={{
                    padding: '8px 14px', borderRadius: 6, border: `1px solid ${COLORS.border}`,
                    background: COLORS.white, color: COLORS.text, fontSize: 12, cursor: 'pointer',
                  }}
                >
                  ← Back to Configs
                </button>
              </div>

              {searchResults.map((result) => (
                <div
                  key={result.id}
                  style={{
                    border: `1px solid ${result.is_flagged ? '#fbbf24' : COLORS.border}`,
                    borderRadius: 8, padding: 16, marginBottom: 12, background: COLORS.white,
                    borderLeft: result.is_flagged ? '4px solid #f59e0b' : undefined,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: COLORS.dark, lineHeight: 1.4 }}>
                          {result.title}
                        </h4>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                        {result.article_type && (
                          <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#e0f2fe', color: '#0369a1' }}>
                            {result.article_type}
                          </span>
                        )}
                        {result.external_id && (
                          <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#f3e8ff', color: '#7c3aed' }}>
                            PMID: {result.external_id}
                          </span>
                        )}
                        {result.is_reviewed && (
                          <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#dcfce7', color: '#15803d' }}>
                            Reviewed
                          </span>
                        )}
                        {result.is_flagged && (
                          <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#fef3c7', color: '#b45309' }}>
                            Flagged
                          </span>
                        )}
                        {result.linked_protocol_id && (
                          <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#dcfce7', color: '#15803d' }}>
                            Linked to Protocol
                          </span>
                        )}
                        {result.source_api && (
                          <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#fef3c7', color: '#b45309' }}>
                            {result.source_api}
                          </span>
                        )}
                        {result.is_open_access && (
                          <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#dcfce7', color: '#15803d' }}>
                            Open Access
                          </span>
                        )}
                        {result.citation_count > 0 && (
                          <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#fee2e2', color: '#dc2626' }}>
                            {result.citation_count} citations
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 4 }}>
                        {result.authors && <span>{result.authors.substring(0, 100)}{result.authors.length > 100 ? '...' : ''} — </span>}
                        <em>{result.journal || 'Unknown Journal'}</em>
                        {result.publication_date && ` (${result.publication_date})`}
                      </div>
                      {result.abstract && (
                        <p style={{ margin: '6px 0', fontSize: 12, color: COLORS.text, lineHeight: 1.5 }}>
                          {result.abstract.substring(0, 250)}{result.abstract.length > 250 ? '...' : ''}
                        </p>
                      )}
                      {result.doi && (
                        <a
                          href={`https://doi.org/${result.doi}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 11, color: COLORS.primary, textDecoration: 'none' }}
                        >
                          DOI: {result.doi}
                        </a>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginLeft: 12, minWidth: 120 }}>
                      <button
                        onClick={() => handleReviewResult(result.id, true, result.is_flagged, result.review_notes)}
                        disabled={result.is_reviewed}
                        style={{
                          padding: '5px 10px', borderRadius: 4, border: `1px solid ${COLORS.border}`,
                          background: result.is_reviewed ? '#dcfce7' : COLORS.white,
                          color: result.is_reviewed ? '#15803d' : COLORS.text, fontSize: 11, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        <CheckCircle size={12} /> {result.is_reviewed ? 'Reviewed' : 'Mark Reviewed'}
                      </button>
                      <button
                        onClick={() => handleReviewResult(result.id, result.is_reviewed, !result.is_flagged, result.review_notes)}
                        style={{
                          padding: '5px 10px', borderRadius: 4, border: `1px solid ${result.is_flagged ? '#f59e0b' : COLORS.border}`,
                          background: result.is_flagged ? '#fef3c7' : COLORS.white,
                          color: result.is_flagged ? '#b45309' : COLORS.text, fontSize: 11, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        <Flag size={12} /> {result.is_flagged ? 'Unflag' : 'Flag'}
                      </button>
                      {!result.linked_protocol_id && (
                        <button
                          onClick={() => handleImportToProtocol(result.id)}
                          style={{
                            padding: '5px 10px', borderRadius: 4, border: 'none',
                            background: COLORS.primary, color: COLORS.white, fontSize: 11,
                            fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                          }}
                        >
                          <Plus size={12} /> Import to Protocol
                        </button>
                      )}
                      {result.url && (
                        <a
                          href={result.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            padding: '5px 10px', borderRadius: 4, border: `1px solid ${COLORS.border}`,
                            background: COLORS.white, color: COLORS.primary, fontSize: 11, textDecoration: 'none',
                            textAlign: 'center', display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center',
                          }}
                        >
                          <ExternalLink size={12} /> View
                        </a>
                      )}
                      <button
                        onClick={() => handleDownloadArticlePDF(result.id)}
                        style={{
                          padding: '5px 10px', borderRadius: 4, border: `1px solid ${result.pdf_url ? '#dc2626' : COLORS.border}`,
                          background: result.pdf_url ? '#fef2f2' : COLORS.white,
                          color: result.pdf_url ? '#dc2626' : COLORS.textLight,
                          fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        <Download size={12} /> {result.pdf_url ? 'PDF' : 'Find PDF'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {searchResults.length === 0 && !sourcesLoading && (
                <div style={{ textAlign: 'center', padding: 40, color: COLORS.textLight }}>
                  <BookOpen size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
                  <p style={{ margin: 0, fontSize: 13 }}>No search results yet</p>
                  <p style={{ margin: '4px 0 0', fontSize: 12 }}>Run a search to fetch results from PubMed, CrossRef, Europe PMC, and Semantic Scholar</p>
                </div>
              )}
            </div>
          )}

          {/* New Search Config Modal */}
          {showNewConfigModal && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
            }}>
              <div style={{
                background: COLORS.white, borderRadius: 12, padding: 28, width: 560,
                maxHeight: '80vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: COLORS.dark }}>New Automated Search</h3>
                  <button onClick={() => setShowNewConfigModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.textLight }}>
                    <X size={20} />
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, display: 'block', marginBottom: 4 }}>Search Name *</label>
                    <input
                      type="text"
                      value={newConfig.name}
                      onChange={(e) => setNewConfig(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Oncology Immunotherapy Trials 2026"
                      style={{ width: '100%', padding: '8px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, display: 'block', marginBottom: 4 }}>Search Terms * (PubMed query)</label>
                    <textarea
                      value={newConfig.search_terms}
                      onChange={(e) => setNewConfig(prev => ({ ...prev, search_terms: e.target.value }))}
                      placeholder='e.g., "immunotherapy" AND "non-small cell lung cancer" AND "checkpoint inhibitor"'
                      rows={3}
                      style={{ width: '100%', padding: '8px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, display: 'block', marginBottom: 4 }}>Condition Filter</label>
                      <input
                        type="text"
                        value={newConfig.condition_filter}
                        onChange={(e) => setNewConfig(prev => ({ ...prev, condition_filter: e.target.value }))}
                        placeholder="e.g., Type 2 Diabetes"
                        style={{ width: '100%', padding: '8px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, display: 'block', marginBottom: 4 }}>Drug Filter</label>
                      <input
                        type="text"
                        value={newConfig.drug_filter}
                        onChange={(e) => setNewConfig(prev => ({ ...prev, drug_filter: e.target.value }))}
                        placeholder="e.g., Pembrolizumab"
                        style={{ width: '100%', padding: '8px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, display: 'block', marginBottom: 4 }}>MeSH Terms</label>
                      <input
                        type="text"
                        value={newConfig.mesh_terms}
                        onChange={(e) => setNewConfig(prev => ({ ...prev, mesh_terms: e.target.value }))}
                        placeholder="e.g., Neoplasms, Immunotherapy"
                        style={{ width: '100%', padding: '8px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, display: 'block', marginBottom: 4 }}>Study Type</label>
                      <select
                        value={newConfig.study_type_filter}
                        onChange={(e) => setNewConfig(prev => ({ ...prev, study_type_filter: e.target.value }))}
                        style={{ width: '100%', padding: '8px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}
                      >
                        <option value="">All Types</option>
                        <option value="clinical_trial">Clinical Trial</option>
                        <option value="meta_analysis">Meta-Analysis</option>
                        <option value="rct">Randomized Controlled Trial</option>
                        <option value="cohort">Cohort Study</option>
                        <option value="review">Systematic Review</option>
                        <option value="guideline">Practice Guideline</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, display: 'block', marginBottom: 4 }}>Frequency</label>
                      <select
                        value={newConfig.frequency}
                        onChange={(e) => setNewConfig(prev => ({ ...prev, frequency: e.target.value }))}
                        style={{ width: '100%', padding: '8px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Biweekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, display: 'block', marginBottom: 4 }}>Date Range (months)</label>
                      <input
                        type="number"
                        value={newConfig.date_range_months}
                        onChange={(e) => setNewConfig(prev => ({ ...prev, date_range_months: parseInt(e.target.value) || 6 }))}
                        min={1} max={60}
                        style={{ width: '100%', padding: '8px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, display: 'block', marginBottom: 4 }}>
                      Target Institutions (comma-separated IDs, blank = all)
                    </label>
                    <input
                      type="text"
                      value={newConfig.institution_ids}
                      onChange={(e) => setNewConfig(prev => ({ ...prev, institution_ids: e.target.value }))}
                      placeholder="e.g., 1,2,5 (leave blank to search all)"
                      style={{ width: '100%', padding: '8px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}
                    />
                    {institutions.length > 0 && (
                      <div style={{ marginTop: 6, fontSize: 11, color: COLORS.textLight }}>
                        Available: {institutions.map(i => `${i.id}=${i.short_name || i.name}`).join(', ')}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20, paddingTop: 16, borderTop: `1px solid ${COLORS.border}` }}>
                  <button
                    onClick={() => setShowNewConfigModal(false)}
                    style={{ padding: '10px 20px', border: `1px solid ${COLORS.border}`, borderRadius: 6, background: COLORS.white, color: COLORS.text, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateSearchConfig}
                    disabled={!newConfig.name || !newConfig.search_terms}
                    style={{
                      padding: '10px 20px', border: 'none', borderRadius: 6,
                      background: (!newConfig.name || !newConfig.search_terms) ? '#cbd5e1' : COLORS.primary,
                      color: COLORS.white, fontSize: 13, fontWeight: 600,
                      cursor: (!newConfig.name || !newConfig.search_terms) ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Create Search Configuration
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cross-Check Tab */}
      {activeTab === 'cross-check' && (
        <div>
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <input
                type="text"
                placeholder="Search by condition name or ICD-10 code..."
                value={crossCheckQuery}
                onChange={(e) => setCrossCheckQuery(e.target.value)}
                style={{
                  flex: 1,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  padding: '10px 12px',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
              <button
                onClick={handleCrossCheck}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: 6,
                  background: COLORS.primary,
                  color: COLORS.white,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!loading) e.target.style.background = COLORS.dark;
                }}
                onMouseLeave={(e) => {
                  if (!loading) e.target.style.background = COLORS.primary;
                }}
              >
                Search
              </button>
            </div>
          </div>

          {crossCheckResults && (
            <div>
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.dark, marginBottom: 12 }}>
                  Matching Protocols ({crossCheckResults.protocols?.length || 0})
                </h3>
                {crossCheckResults.protocols && crossCheckResults.protocols.length > 0 ? (
                  <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: COLORS.light, borderBottom: `1px solid ${COLORS.border}` }}>
                          <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                            Title
                          </th>
                          <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                            Condition
                          </th>
                          <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                            Status
                          </th>
                          <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                            Grade
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {crossCheckResults.protocols.map((protocol) => (
                          <tr key={protocol.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                            <td style={{ padding: 12, fontSize: 13, color: COLORS.text, fontWeight: 500 }}>
                              {protocol.title}
                            </td>
                            <td style={{ padding: 12, fontSize: 13, color: COLORS.textLight }}>
                              {protocol.conditionName}
                            </td>
                            <td style={{ padding: 12 }}>
                              <Badge
                                text={protocol.status}
                                bgColor={STATUS_COLORS[protocol.status]?.bg || COLORS.light}
                                textColor={STATUS_COLORS[protocol.status]?.text || COLORS.text}
                              />
                            </td>
                            <td style={{ padding: 12, fontSize: 13, fontWeight: 600, color: COLORS.primary }}>
                              {protocol.grade}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div
                    style={{
                      padding: 20,
                      background: COLORS.light,
                      borderRadius: 8,
                      color: COLORS.textLight,
                      fontSize: 13,
                    }}
                  >
                    No matching protocols found
                  </div>
                )}
              </div>

              <div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.dark, marginBottom: 12 }}>
                  Matching Treatment Records ({crossCheckResults.treatments?.length || 0})
                </h3>
                {crossCheckResults.treatments && crossCheckResults.treatments.length > 0 ? (
                  <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: COLORS.light, borderBottom: `1px solid ${COLORS.border}` }}>
                          <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                            Date
                          </th>
                          <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                            Patient
                          </th>
                          <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                            Condition
                          </th>
                          <th style={{ padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                            Outcome
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {crossCheckResults.treatments.map((treatment) => (
                          <tr key={treatment.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                            <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>
                              {new Date(treatment.date).toLocaleDateString()}
                            </td>
                            <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>
                              {treatment.patientName}
                            </td>
                            <td style={{ padding: 12, fontSize: 13, color: COLORS.textLight }}>
                              {treatment.condition}
                            </td>
                            <td style={{ padding: 12 }}>
                              <Badge
                                text={treatment.outcome}
                                bgColor={COLORS.light}
                                textColor={COLORS.text}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div
                    style={{
                      padding: 20,
                      background: COLORS.light,
                      borderRadius: 8,
                      color: COLORS.textLight,
                      fontSize: 13,
                    }}
                  >
                    No matching treatment records found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Documents Tab (within detail view) */}
      {activeTab === 'documents' && showDetailView && selectedProtocol && (
        <DocumentsSection
          protocolId={selectedProtocol.id}
          documents={documents}
          onUpload={handleUploadDocument}
          onDownload={handleDownloadDocument}
          onDelete={handleDeleteDocument}
          isLoading={loading}
        />
      )}

      {/* Reports Tab (within detail view) */}
      {activeTab === 'reports' && showDetailView && selectedProtocol && (
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.dark, marginBottom: 16 }}>
            Generate Reports
          </h3>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={handleGeneratePDFReport}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 20px',
                border: `1px solid ${COLORS.primary}`,
                borderRadius: 6,
                background: COLORS.white,
                color: COLORS.primary,
                fontSize: 13,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                if (!loading) e.target.style.background = '#f0f7fb';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = COLORS.white;
              }}
            >
              <FileText size={14} />
              Generate PDF
            </button>
            <button
              onClick={handleGenerateDOCXReport}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 20px',
                border: `1px solid ${COLORS.primary}`,
                borderRadius: 6,
                background: COLORS.white,
                color: COLORS.primary,
                fontSize: 13,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                if (!loading) e.target.style.background = '#f0f7fb';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = COLORS.white;
              }}
            >
              <FileText size={14} />
              Generate Word
            </button>
          </div>
        </div>
      )}

      {/* Detail View Overlay */}
      {showDetailView && selectedProtocol && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
            padding: 20,
          }}
        >
          <div
            style={{
              background: COLORS.white,
              borderRadius: 12,
              padding: 32,
              maxWidth: 800,
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px rgba(0,0,0,0.15)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: COLORS.dark }}>
                {selectedProtocol.title}
              </h2>
              <button
                onClick={() => setShowDetailView(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  color: COLORS.textLight,
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: COLORS.dark, marginBottom: 12 }}>
                Overview
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4 }}>
                    Condition
                  </div>
                  <div style={{ fontSize: 13, color: COLORS.text }}>
                    {selectedProtocol.conditionName}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4 }}>
                    ICD-10 Code
                  </div>
                  <div style={{ fontSize: 13, color: COLORS.text }}>
                    {selectedProtocol.icd10Code}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4 }}>
                    Status
                  </div>
                  <Badge
                    text={selectedProtocol.status}
                    bgColor={STATUS_COLORS[selectedProtocol.status]?.bg || COLORS.light}
                    textColor={STATUS_COLORS[selectedProtocol.status]?.text || COLORS.text}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4 }}>
                    Grade
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.primary }}>
                    {selectedProtocol.grade}
                  </div>
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4 }}>
                  Description
                </div>
                <div style={{ fontSize: 13, color: COLORS.textLight, lineHeight: 1.6 }}>
                  {selectedProtocol.description}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  paddingTop: 12,
                  borderTop: `1px solid ${COLORS.border}`,
                }}
              >
                <button
                  onClick={() => handleCopyToClipboard(JSON.stringify(selectedProtocol, null, 2))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 12px',
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 4,
                    background: COLORS.white,
                    color: COLORS.primary,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <Copy size={12} />
                  Copy Details
                </button>
                <button
                  onClick={() => handleDeleteProtocol(selectedProtocol.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 12px',
                    border: `1px solid ${COLORS.red}`,
                    borderRadius: 4,
                    background: COLORS.white,
                    color: COLORS.red,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <Trash2 size={12} />
                  Delete Protocol
                </button>
              </div>
            </div>

            {/* Tab selector in detail view */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', gap: 12, borderBottom: `1px solid ${COLORS.border}`, marginBottom: 16 }}>
                <button
                  onClick={() => setActiveTab('documents')}
                  style={{
                    padding: '8px 12px',
                    border: 'none',
                    background: 'none',
                    fontSize: 13,
                    fontWeight: 600,
                    color: activeTab === 'documents' ? COLORS.primary : COLORS.textLight,
                    cursor: 'pointer',
                    borderBottom: activeTab === 'documents' ? `2px solid ${COLORS.primary}` : 'none',
                    marginBottom: -16,
                  }}
                >
                  Documents
                </button>
                <button
                  onClick={() => setActiveTab('reports')}
                  style={{
                    padding: '8px 12px',
                    border: 'none',
                    background: 'none',
                    fontSize: 13,
                    fontWeight: 600,
                    color: activeTab === 'reports' ? COLORS.primary : COLORS.textLight,
                    cursor: 'pointer',
                    borderBottom: activeTab === 'reports' ? `2px solid ${COLORS.primary}` : 'none',
                    marginBottom: -16,
                  }}
                >
                  Reports
                </button>
              </div>
            </div>

            {activeTab === 'documents' && (
              <DocumentsSection
                protocolId={selectedProtocol.id}
                documents={documents}
                onUpload={handleUploadDocument}
                onDownload={handleDownloadDocument}
                onDelete={handleDeleteDocument}
                isLoading={loading}
              />
            )}

            {activeTab === 'reports' && (
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.dark, marginBottom: 16 }}>
                  Generate Reports
                </h3>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    onClick={handleGeneratePDFReport}
                    disabled={loading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '12px 20px',
                      border: `1px solid ${COLORS.primary}`,
                      borderRadius: 6,
                      background: COLORS.white,
                      color: COLORS.primary,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    <FileText size={14} />
                    Generate PDF
                  </button>
                  <button
                    onClick={handleGenerateDOCXReport}
                    disabled={loading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '12px 20px',
                      border: `1px solid ${COLORS.primary}`,
                      borderRadius: 6,
                      background: COLORS.white,
                      color: COLORS.primary,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    <FileText size={14} />
                    Generate Word
                  </button>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24, paddingTop: 16, borderTop: `1px solid ${COLORS.border}` }}>
              <button
                onClick={() => setShowDetailView(false)}
                style={{
                  padding: '10px 20px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  background: COLORS.white,
                  color: COLORS.text,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = COLORS.light;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = COLORS.white;
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <NewProtocolModal
        isOpen={showNewProtocolModal}
        onClose={() => setShowNewProtocolModal(false)}
        onSave={handleCreateProtocol}
        isLoading={loading}
      />

      <NewTreatmentModal
        isOpen={showNewTreatmentModal}
        onClose={() => setShowNewTreatmentModal(false)}
        onSave={handleCreateTreatment}
        isLoading={loading}
      />

      {/* Floating action buttons — Search & Translate */}
      <div style={{ position: 'fixed', bottom: 20, right: 20, display: 'flex', flexDirection: 'column', gap: 10, zIndex: 9999 }}>
        <button
          onClick={() => { setShowSearch(prev => !prev); if (showTranslation) setShowTranslation(false); }}
          title="Research Search"
          style={{
            width: 48, height: 48, borderRadius: '50%', border: 'none', cursor: 'pointer',
            background: showSearch ? '#1e3a5f' : 'linear-gradient(135deg, #2563eb, #1e3a5f)',
            color: '#fff', fontSize: 20,
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {showSearch ? '✕' : '🔍'}
        </button>
        <button
          onClick={() => { setShowTranslation(prev => !prev); if (showSearch) setShowSearch(false); }}
          title="Translate"
          style={{
            width: 48, height: 48, borderRadius: '50%', border: 'none', cursor: 'pointer',
            background: showTranslation ? '#285e61' : 'linear-gradient(135deg, #2c7a7b, #285e61)',
            color: '#fff', fontSize: 22,
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {showTranslation ? '✕' : '🌐'}
        </button>
      </div>
      <ManualSearchPanel isOpen={showSearch} onClose={() => setShowSearch(false)} />
      <TranslationPanel isOpen={showTranslation} onClose={() => setShowTranslation(false)} />
    </div>
  );
}
